import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Image } from '$partials/media';
import { linearScale } from '$utilities/scale';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const CategoryFilter = ({
    category = null,
    setCategory = null
}) => {
    if (!category || !setCategory) {
        return null;
    }

    return (
        <form>
            <fieldset >
                <legend className='sr-only'>Filter the nominee categories list by 1 of these types</legend>

                <div className='flex gap-5'>
                    <div>
                        <input
                            checked={category === 'all'}
                            className='sr-only'
                            id='frm-category-all'
                            name='category'
                            onChange={setCategory.bind(this, 'all')}
                            type='radio'
                            value='all' />

                        <label
                            className={`block type-style-awards-tiny rounded px-20 py-15 cursor-pointer custom-focus-ring${category === 'all' ? ' bg-orange text-white' : ' bg-white text-orange'}`}
                            htmlFor='frm-category-all'>
                            All
                        </label>
                    </div>

                    <div>
                        <input
                            checked={category === 'athletes'}
                            className='sr-only'
                            id='frm-category-athletes'
                            name='category'
                            onChange={setCategory.bind(this, 'athletes')}
                            type='radio'
                            value='athletes' />

                        <label
                            className={`block type-style-awards-tiny rounded px-20 py-15 cursor-pointer custom-focus-ring${category === 'athletes' ? ' bg-orange text-white' : ' bg-white text-orange'}`}
                            htmlFor='frm-category-athletes'>
                            Athletes
                        </label>
                    </div>

                    <div>
                        <input
                            checked={category === 'club'}
                            className='sr-only'
                            id='frm-category-club'
                            name='category'
                            onChange={setCategory.bind(this, 'club')}
                            type='radio'
                            value='club' />

                        <label
                            className={`block type-style-awards-tiny rounded px-20 py-15 cursor-pointer custom-focus-ring${category === 'club' ? ' bg-orange text-white' : ' bg-white text-orange'}`}
                            htmlFor='frm-category-club'>
                            Clubs
                        </label>
                    </div>
                </div>
            </fieldset>
        </form>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const AwardsModuleNomineesLandingHeader = ({
    bannerCopy = '',
    bannerTitle = '',
    filterCategory = null,
    images = [],
    logo = null,
    setFilterCategory = null
}) => {
    // misc: chunk images as per design
    const desktopImageGroup1 = images.slice(0, 2);
    const desktopImageGroup2 = images.slice(2, 5);
    const _desktopImages = [];

    if (desktopImageGroup1.length > 0) {
        _desktopImages.push(desktopImageGroup1);
    }

    if (desktopImageGroup2.length > 0) {
        _desktopImages.push(desktopImageGroup2);
    }

    const tabletImageGroup1 = images.slice(0, 2);
    const tabletImageGroup2 = images.slice(2, 4);
    const _tabletImages = [];

    if (tabletImageGroup1.length > 0) {
        _tabletImages.push(tabletImageGroup1);
    }

    if (tabletImageGroup2.length > 0) {
        _tabletImages.push(tabletImageGroup2);
    }

    const mobileImageGroup1 = images.slice(0, 2);
    const mobileImageGroup2 = images.slice(2, 4);
    const _mobileImages = [];

    if (mobileImageGroup1.length > 0) {
        _mobileImages.push(mobileImageGroup1);
    }

    if (mobileImageGroup2.length > 0) {
        _mobileImages.push(mobileImageGroup2);
    }

    // references
    const desktopImageGroup1Ref = useRef();
    const desktopImageGroup2Ref = useRef();

    const tabletImageGroup1Ref = useRef();
    const tabletImageGroup2Ref = useRef();
    const tabletLogoRef = useRef();

    const mobileImageGroup1Ref = useRef();
    const mobileImageGroup2Ref = useRef();
    const moduleRef = useRef();

    // state
    const [ screenSize, setScreenSize ] = useState('mobile');

    // methods
    const handleResize = () => {
        if (window.innerWidth < 768) {
            if (screenSize !== 'mobile') {
                setScreenSize('mobile');
            }
        } else if ((window.innerWidth < 1024) && (window.innerWidth >= 768)){
            if (screenSize !== 'tablet') {
                setScreenSize('tablet');
            }
        }
        else {
            if (screenSize !== 'desktop') {
                setScreenSize('desktop');
            }
        }
    };

    const handleScroll = () => {
        // desktop: image group 1 transform
        if (desktopImageGroup1Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight * 1.5, 0, 10); // * 1.5 to appear slower in transition
            desktopImageGroup1Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // desktop: image group 2 transform
        if (desktopImageGroup2Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 10);
            desktopImageGroup2Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // tablet: logo transform
        if (tabletLogoRef.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 50);
            tabletLogoRef.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // tablet: image group 1 transform
        if (tabletImageGroup1Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight * 1.5, 0, 10); // * 1.5 to appear slower in transition
            tabletImageGroup1Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // tablet: image group 2 transform
        if (tabletImageGroup2Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 10);
            tabletImageGroup2Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });

        handleResize();

        // component unmount
        return () => {
            window.removeEventListener('resize', handleResize, { passive: true });
            window.removeEventListener('scroll', handleScroll, { passive: true });
        };
    }, [ screenSize ]);

    // misc: helper array for rendering later
    const imageDesktopRefArray = [
        desktopImageGroup1Ref,
        desktopImageGroup2Ref,
    ];

    const imageTabletRefArray = [
        tabletImageGroup1Ref,
        tabletImageGroup2Ref,
    ];

    const imageMobileRefArray = [
        mobileImageGroup1Ref,
        mobileImageGroup2Ref,
    ];

    // render
    return (
        <section ref={moduleRef}>
            <div className='mt-60 w-full overflow-hidden relative z-10'>
                {/* mobile */}
                {screenSize === 'mobile' && (
                    <div className='layout-container text-white'>
                        {/* image grid 1 */}
                        {/* logo */}
                        <div className='w-full mb-20 relative px-10 pointer-events-none select-none'>
                            {logo && (
                                <img
                                    alt={logo.alt}
                                    className='w-[278px] -ml-15'
                                    height={logo.height}
                                    loading='eager'
                                    role='presentation'
                                    src={logo.src}
                                    width={logo.width} />
                            )}
                        </div>

                        <div className='flex pointer-events-none select-none'>
                            {/* left col */}
                            {mobileImageGroup1.length > 0 && (
                                <ul
                                    className='w-7/12 mt-30 px-10'
                                    ref={imageMobileRefArray[0]}>
                                    {mobileImageGroup1.map((item, idx) => {
                                        return (
                                            <li
                                                className='mb-20 -ml-20 aspect-[207/181]'
                                                key={idx}>
                                                <Image
                                                    {...item.image}
                                                    className={'h-full w-full object-cover'}
                                                    containerClassName={'h-full w-full'}
                                                    containerStyle={{
                                                        height: '100%',
                                                        paddingBottom: 0,
                                                    }}
                                                    role='presentation'
                                                    src={item.image?.src}/>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {/* right col */}
                            {mobileImageGroup2.length > 0 && (
                                <ul
                                    className='w-5/12 px-10'
                                    ref={imageMobileRefArray[1]}>
                                    {mobileImageGroup2.map((item, idx) => {
                                        return (
                                            <li
                                                className={`mb-20 -mr-150 ${idx === 0 ? 'aspect-[296/164]' : 'aspect-[1/1]'}`}
                                                key={idx}>
                                                <Image
                                                    {...item.image}
                                                    className={'h-full w-full object-cover'}
                                                    containerClassName={'h-full w-full'}
                                                    containerStyle={{
                                                        height: '100%',
                                                        paddingBottom: 0
                                                    }}
                                                    role='presentation'
                                                    src={item.image?.src}/>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        {bannerTitle && (
                            <h1 className='type-style-2 mt-20 mb-30 px-10'>{bannerTitle}</h1>
                        )}

                        {bannerCopy && (
                            <p className='type-style-5 mb-40 px-10'>{bannerCopy}</p>
                        )}

                        {/* category filter */}
                        <div className='mb-60 mt-40 px-10'>
                            <CategoryFilter
                                category={filterCategory}
                                setCategory={setFilterCategory} />
                        </div>
                    </div>
                )}

                {/* tablet */}
                {screenSize === 'tablet' && (
                    <div className='layout-container text-white z-10 relative'>
                        <div className='h-auto max-h-[515px]'>
                            <div className='h-full flex'>
                                {/* left col */}
                                <div className='h-full w-1/2 flex flex-col px-20'>
                                    {logo && (
                                        <div>
                                            <img
                                                className='w-[318px] -ml-15 my-auto pointer-events-none select-none'
                                                height={logo.height}
                                                loading='eager'
                                                role='presentation'
                                                src={logo.src}
                                                width={logo.width} />
                                        </div>
                                    )}

                                    {bannerTitle && (
                                        <h1 className='type-style-2 mt-80'>{bannerTitle}</h1>
                                    )}

                                    {bannerCopy && (
                                        <p className='type-style-5 mt-20'>{bannerCopy}</p>
                                    )}

                                    {/* category filter */}
                                    <div className='mb-60 mt-40'>
                                        <CategoryFilter
                                            category={filterCategory}
                                            setCategory={setFilterCategory} />
                                    </div>
                                </div>

                                {/* right col */}
                                <div className='h-full w-1/2 mt-30 pointer-events-none select-none'>
                                    <div className='flex'>
                                        {/* image grid */}
                                        {_desktopImages && _desktopImages.length > 0 && (
                                            _desktopImages.map((chunk, idx) => {
                                                return (
                                                    <ul
                                                        className={`${idx === 0 ? 'mt-40' : ''} ${idx === 0 ? 'pl-20 pr-10 w-9/12' : 'px-10 w-2/6'}`}
                                                        key={idx}
                                                        ref={imageTabletRefArray[idx]}>
                                                        {chunk.map((item, jdx) => {
                                                            return (
                                                                <li
                                                                    className={`mb-20 ${idx === 1 ? '-mr-240' : ''}
                                                                    ${idx === 0
                                                                    ? jdx === 0
                                                                        ? 'aspect-[225/197]'
                                                                        : 'aspect-[225/211]'
                                                                    : jdx === 0
                                                                        ? 'aspect-[322/214]'
                                                                        : 'aspect-[275/234]'}`}
                                                                    key={`${idx}-${jdx}`}>
                                                                    <div className='h-full w-full'>
                                                                        <Image
                                                                            {...item.image}
                                                                            className={'h-full w-full object-cover'}
                                                                            containerClassName={'h-full w-full'}
                                                                            containerStyle={{
                                                                                height: '100%',
                                                                                paddingBottom: 0
                                                                            }}
                                                                            role='presentation'
                                                                            src={item.image?.src}/>
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                );
                                            }))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* desktop */}
                {screenSize === 'desktop' && (
                    <div className='h-[700px] layout-container text-white z-10 relative'>
                        <div className='h-full'>
                            <div className='h-full flex flex-wrap'>
                                {/* left col */}
                                <div className='h-full w-full md:w-7/12 flex flex-col px-10 sm:px-20'>
                                    {logo && (
                                        <div>
                                            <img
                                                className='w-[278px] md:w-[497px] md:-ml-25 my-auto pointer-events-none select-none'
                                                height={logo.height}
                                                loading='eager'
                                                role='presentation'
                                                src={logo.src}
                                                width={logo.width} />
                                        </div>
                                    )}

                                    {bannerTitle && (
                                        <h1 className='type-style-2 md:mt-80'>{bannerTitle}</h1>
                                    )}

                                    {bannerCopy && (
                                        <p className='type-style-5 md:mt-20'>{bannerCopy}</p>
                                    )}

                                    {/* category filter */}
                                    <div className='mt-40'>
                                        <CategoryFilter
                                            category={filterCategory}
                                            setCategory={setFilterCategory} />
                                    </div>
                                </div>

                                {/* right col */}
                                <div className='h-full w-full md:w-5/12 pointer-events-none select-none'>
                                    <div className='flex relative'>
                                        {/* image grid */}
                                        {_desktopImages && _desktopImages.length > 0 && (
                                            _desktopImages.map((chunk, idx) => {
                                                return (
                                                    <ul
                                                        className={`px-20 ${idx === 0 ? 'mt-80 w-4/5' : 'w-1/5'}`}
                                                        key={idx}
                                                        ref={imageDesktopRefArray[idx]}>
                                                        {chunk.map((item, jdx) => {
                                                            return (
                                                                <li
                                                                    className={`mb-40 ${idx === 0
                                                                        ? jdx === 0
                                                                            ? 'aspect-[363/317]'
                                                                            : 'aspect-[363/317]'
                                                                        : jdx === 0
                                                                            ? 'aspect-[363/201] -mr-300'
                                                                            : jdx === 1
                                                                                ? 'aspect-[363/263] -mr-300'
                                                                                : 'aspect-[363/201] -mr-300'}`}
                                                                    key={`${idx}-${jdx}`}>
                                                                    <div className='h-full w-full'>
                                                                        <Image
                                                                            {...item.image}
                                                                            className={'h-full w-full object-cover'}
                                                                            containerClassName={'h-full w-full'}
                                                                            containerStyle={{
                                                                                height: '100%',
                                                                                paddingBottom: 0
                                                                            }}
                                                                            role='presentation'
                                                                            src={item.image?.src}/>
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                );
                                            }))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className='sm:h-[413px] md:h-[524px] h-[652px] bg-black left-1/2 -translate-x-1/2 absolute bottom-0 w-full -z-10'></div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const AwardsModuleNomineesLandingHeaderPropTypes = {
    bannerCopy: PropTypes.string,
    bannerTitle: PropTypes.string,
    filterCategory: PropTypes.string.isRequired,
    images: PropTypes.array,
    logo: PropTypes.object.isRequired,
    setFilterCategory: PropTypes.func.isRequired
};

const CategoryFilterPropTypes = {
    category: PropTypes.string.isRequired,
    setCategory: PropTypes.func.isRequired
};

AwardsModuleNomineesLandingHeader.propTypes = AwardsModuleNomineesLandingHeaderPropTypes;
CategoryFilter.propTypes = CategoryFilterPropTypes;

export default AwardsModuleNomineesLandingHeader;
